/**
 * Camada 1 · o conjunto de ícones da plataforma.
 *
 * PORTADO DO LEGADO `prontidaovoo` em 29/08/2026, arquivo por arquivo, para fechar a linha
 * V03 da paridade. Cada desenho vem com o comentário original que explica POR QUE aquele
 * traço e não outro, e esses comentários são a parte que não se reescreve: eles guardam
 * decisões de leitura que ninguém reconstrói olhando o `path`.
 *
 * POR QUE ISTO SUBIU PARA `packages/ui` EM VEZ DE FICAR NO APP: um ícone que mora no produto
 * é um ícone que o próximo produto redesenha, e aí a família deixa de parecer uma família. O
 * `ProductConfig` nomeia o ícone, e é aqui que o nome vira desenho.
 *
 * O QUE ISTO SUBSTITUI, e por que a troca importa: antes o rail renderizava o nome do ícone
 * como TEXTO, então o manifesto usava emoji. Emoji muda de desenho por sistema operacional, e
 * num produto de aviação o ícone de aeronave precisa ser o mesmo no Mac, no Windows e no
 * Android. Não é gosto: é reconhecimento em 20 pixels.
 *
 * SVG inline, sem biblioteca de ícones: são poucos, e uma dependência a mais custaria peso de
 * pacote e mais uma coisa para atualizar. Todos herdam a cor do texto por currentColor.
 */

import type { ComponentType, SVGProps } from 'react'
type PropsIcone = SVGProps<SVGSVGElement>

// Base comum: 24x24, traço de 1.6 e pontas arredondadas. Concentrar aqui evita ícone com
// espessura diferente dos vizinhos, que é o defeito mais visível de ícone desenhado à mão.
function Base({ children, ...resto }: PropsIcone) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...resto}
    >
      {children}
    </svg>
  )
}

// COPIAR: os DOIS RETANGULOS SOBREPOSTOS, que e o simbolo universal.
//
// Pedido dele na origem em 02/09/2026, olhando um NOTAM de Brasilia: "tem que ter botao PARA
// COPIAR O NOTAM (AQUELE QUE SAO DOIS RETANGULOS UM EM CIMA DO OUTRO). SIMBOLO PADRAO DE COPIAR
// (TEM QUE FICAR BEM VISIVEL)".
//
// A folha de tras e RECUADA e nao inteira: desenhada completa, ela some atras da da frente e o
// simbolo perde justamente a leitura de "duas copias", que e o que o faz reconhecivel sem legenda.
export const IconeCopiar = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M4 16V4a2 2 0 0 1 2-2h10" />
    <rect x="8" y="6" width="12" height="16" rx="2" />
  </Base>
)

// VISTO: a confirmacao de que algo deu certo. Mesmo traco e mesma espessura do resto, para a
// troca de um icone pelo outro nao parecer que o botao mudou de tamanho.
export const IconeVisto = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M20 6L9 17l-5-5" />
  </Base>
)

// Diário de bordo: livro aberto com o vinco central e as linhas do registro. É o objeto físico
// que o módulo substitui, e o reconhecimento é imediato sem precisar do rótulo no menu retraído.
export const IconeDiario = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 6.5S10 4.5 4 4.5v13c6 0 8 2 8 2s2-2 8-2v-13c-6 0-8 2-8 2Z" />
    <path d="M12 6.5v15" />
    <path d="M7 9h2M7 12h2M15 9h2M15 12h2" />
  </Base>
)

// Painel: quatro blocos, a leitura mais reconhecível de "visão geral".
export const IconePainel = (p: PropsIcone) => (
  <Base {...p}>
    <rect x="3" y="3" width="7" height="8" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="11" width="7" height="10" rx="1.5" />
  </Base>
)

// Voos: silhueta de avião em rota, não um ícone de decolagem, porque o módulo cobre o voo inteiro.
export const IconeVoos = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M10.5 20.5 12 15l7.2-2.3a2 2 0 0 0 .4-3.6L4.3 3.2a.6.6 0 0 0-.8.8l4.2 8.6-3.9 1.3a1 1 0 0 0-.1 1.9l3.4 1.4 1.4 3.4a1 1 0 0 0 1.9-.1Z" />
  </Base>
)

// Frota: hangar com aeronave, para não repetir o mesmo avião do módulo de voos.
export const IconeFrota = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M3 20V10.5L12 5l9 5.5V20" />
    <path d="M3 20h18" />
    <path d="M8.5 20v-5.5h7V20" />
  </Base>
)

// Tripulação: duas pessoas, porque tripulação nunca é uma só.
// Tripulação: pessoa com a ASA DE BREVÊ, que é o distintivo do aeronauta. É o símbolo que
// qualquer pessoa da aviação reconhece, e é o que separa este item de "usuários do sistema":
// aqui são as pessoas que VOAM, e não as que têm conta.
export const IconeTripulacao = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="6.5" r="3" />
    <path d="M7 21a5 5 0 0 1 10 0" />
    {/* As duas asas saindo dos ombros, na horizontal: é a leitura da asa de brevê em tamanho
        pequeno. Detalhe de pena não sobrevive a 20 pixels, e virar mancha seria pior. */}
    <path d="M9 13.5 2.5 15.5 9 16.5M15 13.5l6.5 2-6.5 1" />
  </Base>
)

// Meteorologia: nuvem com chuva, o desenho que ninguém confunde.
export const IconeMeteorologia = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M7 16.5a4 4 0 0 1 .5-8 5.2 5.2 0 0 1 9.9 1.3A3.6 3.6 0 0 1 17 16.5Z" />
    <path d="M8.5 19.5 8 21" />
    <path d="M12 19.5 11.5 21" />
    <path d="M15.5 19.5 15 21" />
  </Base>
)

// NOTAM e aeródromos: torre com sinal, que é o que um aviso ao aeronavegante representa.
export const IconeNotam = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 12v9" />
    <path d="M9 21h6" />
    <path d="M9.5 12h5l-1-6h-3Z" />
    <path d="M6.5 6.5a6 6 0 0 1 0-3.5" />
    <path d="M17.5 6.5a6 6 0 0 0 0-3.5" />
  </Base>
)

// Conformidade: prancheta com visto, a metáfora corrente de item verificado.
export const IconeConformidade = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M9 4H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" />
    <rect x="9" y="2.5" width="6" height="3.5" rx="1" />
    <path d="m9 13.5 2 2 4-4.5" />
  </Base>
)

// Segurança operacional: escudo com sinal de atenção. O escudo separa do documento de
// conformidade (prancheta) e do livro de normas: aqui não se cumpre requisito, se protege operação.
export const IconeSeguranca = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 3 4.5 6v6c0 4.2 3.1 7.8 7.5 9 4.4-1.2 7.5-4.8 7.5-9V6L12 3Z" />
    <path d="M12 9v4M12 16h.01" />
  </Base>
)

// Normas: livro aberto, para separar visualmente do documento de conformidade.
export const IconeNormas = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 6.5C10.5 5.2 8.4 4.5 5 4.5V18c3.4 0 5.5.7 7 2 1.5-1.3 3.6-2 7-2V4.5c-3.4 0-5.5.7-7 2Z" />
    <path d="M12 6.5V20" />
  </Base>
)

// Três traços do menu de celular.
export const IconeMenu = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </Base>
)

// Fechar a gaveta.
export const IconeFechar = (p: PropsIcone) => (
  <Base {...p}>
    <path d="m6 6 12 12" />
    <path d="m18 6-12 12" />
  </Base>
)

// Recolher e expandir o menu: o PAINEL LATERAL, e nao mais um pipe com seta.
//
// Pedido dele em 30/08/2026, com a captura da barra do Gemini: "MAS QUERO O ICONE DO GEMINI". O
// desenho anterior era o que ele havia descrito em 28/08 ("um pipe com uma seta para um lado e para
// o outro"), e nunca chegou a ser usado por ninguem: a medicao daquele dia devolveu ZERO chamadas
// de `IconeRetrair` e `IconeExpandir` em todo o repositorio, porque o Rail desenhava os dois
// caracteres tipograficos no lugar. Icone criado e nunca chamado e codigo inerte, entao os dois
// sairam do arquivo em vez de ficarem guardados esperando um uso que nao vinha.
//
// UM icone so, e nao um por estado. O retangulo com a coluna marcada representa o OBJETO (o painel
// lateral), e nao a direcao do gesto, entao ele continua verdadeiro aberto ou fechado. E o que a
// barra do Gemini faz, e e o que evita o desenho piscar entre dois simbolos a cada clique. Quem
// diz o estado e o `aria-expanded` mais o `aria-label` do botao, que mudam nos dois sentidos.
export const IconePainelLateral = (p: PropsIcone) => (
  <Base {...p}>
    {/* A moldura do painel. `rx` de 2 para acompanhar o raio do resto da interface: canto vivo
        aqui destoaria de todo botao e cartao ao redor. */}
    <rect x="3" y="5" width="18" height="14" rx="2" />
    {/* A COLUNA DO MENU, a 6 unidades da borda esquerda. E ela que faz o retangulo ser lido como
        "painel com uma barra lateral", e nao como "imagem" ou "janela". Encostada demais na borda
        ela some no tamanho real de 20px; longe demais, o desenho vira duas colunas iguais. */}
    <path d="M9 5v14" />
  </Base>
)

// Voltar da tela de detalhe para a lista, no celular.
export const IconeVoltar = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M19 12H5" />
    <path d="m11 6-6 6 6 6" />
  </Base>
)

// SUBIR E DESCER NA ORDEM DE UMA LISTA, desenhados em 09/09/2026 para a fila da recepcao.
//
// POR QUE NAO REUSEI O `IconeVoltar` GIRADO POR CSS: uma rotacao em `transform` nao troca o
// significado para quem usa leitor de tela, e em 16 pixels a haste longa do voltar (`M19 12H5`)
// vira um traco atravessando o alvo inteiro. Estas sao mais curtas, e a ponta e o que se le.
//
// A HASTE FICA, e nao e enfeite: uma ponta sozinha (o chevron) e ambigua entre "subir" e
// "expandir", e os dois convivem na mesma tela de configuracoes.
export const IconeSetaCima = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 19V6" />
    <path d="m6 12 6-6 6 6" />
  </Base>
)

export const IconeSetaBaixo = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 5v13" />
    <path d="m6 12 6 6 6-6" />
  </Base>
)

// Sol e lua do alternador de tema: o ícone mostra o tema que o clique vai aplicar.
export const IconeSol = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.9 4.9 1.4 1.4" />
    <path d="m17.7 17.7 1.4 1.4" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m4.9 19.1 1.4-1.4" />
    <path d="m17.7 6.3 1.4-1.4" />
  </Base>
)

export const IconeLua = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </Base>
)

// Porta com seta para fora. Nunca aparece sozinho: a palavra Sair vai junto em toda largura.
export const IconeSair = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M14 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8" />
    <path d="M17 8.5 20.5 12 17 15.5" />
    <path d="M20 12H10" />
  </Base>
)

// Lupa da busca do frame de lista.
export const IconeBusca = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Base>
)

// Mapa: alfinete de posição, a leitura mais direta de "onde estou".
export const IconeMapa = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 21s-6-5.4-6-10.2A6 6 0 0 1 18 10.8C18 15.6 12 21 12 21z" />
    <circle cx="12" cy="10.8" r="2.2" />
  </Base>
)

// Sobre o sistema: círculo com "i", a leitura universal de informação sobre o próprio programa.
export const IconeSobre = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 7.8h.01" />
  </Base>
)

// Chave da IA: chave de fenda cruzada com o brilho de quatro pontas usado para assistente.
// Nem cadeado nem robô: o item abre a tela que guarda a chave, e é isso que o desenho promete.
export const IconeIa = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M6 16.5 3.5 19a1.8 1.8 0 0 0 2.5 2.5L8.5 19" />
    <path d="m7.2 17.8 7-7" />
    <path d="M14.2 4.5 15.6 7.9 19 9.3l-3.4 1.4-1.4 3.4-1.4-3.4L9.4 9.3l3.4-1.4z" />
    <path d="M19.3 15.2l.7 1.6 1.6.7-1.6.7-.7 1.6-.7-1.6-1.6-.7 1.6-.7z" />
  </Base>
)

// Asa em voo: o item de destaque do menu, onde a operação registra decolagem e pouso.
// Traço simples, do mesmo peso dos demais ícones, para o destaque vir do fundo e não de um
// desenho mais pesado que os vizinhos.
export function IconeVoar(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M10.5 13.5 3 11l1-2 8 1.2 4.6-4.6a2.1 2.1 0 0 1 3 3L15 13.2l1.2 8-2 1-2.5-7.5Z" />
    </svg>
  )
}

// Duas pessoas: o cadastro de quem tem acesso ao sistema. Distinto do ícone de Tripulação, que
// é sobre aeronauta, e não sobre conta de acesso: são coisas diferentes e o menu não pode
// sugerir que sejam a mesma.
// Usuários: CRACHÁ com a pessoa dentro. O que este item administra é a conta de acesso, e não a
// pessoa: o contorno do cartão é o que diz isso de relance, e é o que o separa de Tripulação, que
// é sobre quem voa.
export function IconeUsuarios(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      {/* O cartão, com o furo do cordão no topo: é o que faz o desenho ser lido como credencial
          e não como uma foto emoldurada. */}
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
      <path d="M10 2.5h4" />
      <circle cx="9.5" cy="10.5" r="2.2" />
      <path d="M6 16.5a3.5 3.5 0 0 1 7 0" />
      {/* As duas linhas à direita são o texto do crachá: dizem "cadastro" sem precisar de letra. */}
      <path d="M15.5 9.5h3M15.5 12.5h3" />
    </svg>
  )
}


/* ---------------------------------------------------------------------------------------
 * OS DEZESSEIS QUE FALTAVAM, escritos em 30/08/2026.
 *
 * POR QUE ELES NASCERAM: o conjunto acima cobria o prontidaovoo, e o `_template` e outro
 * produto da plataforma continuaram nomeando EMOJI no `product.config`. Como o rail cai para
 * o texto cru quando o nome nao resolve, o emoji chegava desenhado na tela, e um menu lateral
 * INTEIRO era emoji. O fundador mediu isso em 30/08/2026 e fechou a regra: emoji nao
 * entra em app nenhum da plataforma.
 * ------------------------------------------------------------------------------------- */

// Ajuda: a boia salva-vidas, e nao um ponto de interrogacao dentro de um circulo.
// A escolha nao e estetica. Interrogacao em 20px vira um rabisco ilegivel, e o mesmo
// simbolo ja e usado em toda interface para "o que e isto?" ao lado de um campo. A boia diz
// outra coisa, que e o que esta tela faz: quem chega aqui esta com um problema e quer
// socorro, e nao a definicao de um termo.
export const IconeAjuda = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.6" />
    <path d="m5.6 5.6 3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9" />
  </Base>
)

// Sino: a notificacao. O badalo fica separado do corpo porque em 20px um sino com o badalo
// colado le como uma gota, e nao como um sino.
export const IconeSino = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M18 8.5a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16s-2-1.5-2-6.5Z" />
    <path d="M10.3 19a2 2 0 0 0 3.4 0" />
  </Base>
)

// Cadeado fechado: o dado que nao sai daqui. Fechado de proposito, porque cadeado aberto
// significa o oposto do que a frase ao lado promete.
export const IconeCadeado = (p: PropsIcone) => (
  <Base {...p}>
    <rect x="4" y="10.5" width="16" height="10" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    <path d="M12 14.5v2" />
  </Base>
)

// Trilha de auditoria: a folha com as linhas do registro e o carimbo de conferido. O visto
// no canto e o que separa "documento" de "documento auditado".
export const IconeAuditoria = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" />
    <path d="M14 3l4 4v4" />
    <path d="M8.5 9h5M8.5 12.5h3" />
    <path d="M14.5 17.5l2 2 4-4.5" />
  </Base>
)

// Idioma: o globo com o meridiano e o paralelo. E o desenho que a industria inteira usa
// para troca de lingua, e reconhecimento vale mais que originalidade num controle de chassi.
export const IconeIdioma = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18Z" />
  </Base>
)

// Registros: a lista com os marcadores. Tres linhas e nao quatro, porque em 20px a quarta
// vira um borrao cinza.
export const IconeRegistros = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M9 6.5h11M9 12h11M9 17.5h11" />
    <path d="M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" />
  </Base>
)

// Ajustes: a engrenagem simplificada em seis dentes. Doze dentes, que e o desenho classico,
// vira serrilha ilegivel no tamanho do rail.
export const IconeAjustes = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2.5v2.6M12 18.9v2.6M21.5 12h-2.6M5.1 12H2.5M18.7 5.3l-1.8 1.8M7.1 16.9l-1.8 1.8M18.7 18.7l-1.8-1.8M7.1 7.1L5.3 5.3" />
  </Base>
)

// Engrenagem: o contorno com dentes e o furo no centro, que e o desenho de Configuracoes do
// iOS, do Android e do Google. Nasceu em 11/09/2026 por decisao do fundador em enquete: o
// "ajustes" acima, um circulo com oito raios, lia como o sol do botao de tema.
//
// O DESENHO E O DO LUCIDE ("settings", licenca ISC), e nao inventado aqui: e o conjunto de
// icones de traco mais usado em produto, feito na mesma grade de 24 e traco fino desta casa.
// Ele usa DENTES LARGOS E ARREDONDADOS, e e isso que o mantem legivel no tamanho do rail,
// onde a serrilha de doze dentes finos que o comentario do "ajustes" temia vira ruido.
//
// "ajustes" CONTINUA EXISTINDO sem mudanca, porque outros produtos podem usar o nome.
export const IconeEngrenagem = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </Base>
)

// Pessoas: o grupo. Duas cabecas e nao tres, porque a terceira em 20px encosta nas outras
// duas e o conjunto le como uma mancha unica.
export const IconePessoas = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
    <path d="M16.5 5.2a3.2 3.2 0 0 1 0 5.6" />
    <path d="M17.8 14.9c2 .7 3.2 2.5 3.2 5.1" />
  </Base>
)

// CADASTRO: UMA pessoa com o sinal de mais, que e o desenho de "gente nova entrando". Somado
// em 15/09/2026, por decisao do fundador em enquete: no admin, Pessoas e Cadastros usavam o
// MESMO icone `pessoas`, e com o menu recolhido ficavam dois desenhos iguais seguidos, sem
// rotulo para separar.
//
// O DESENHO E O PADRAO DAS BIBLIOTECAS GRANDES, e nao invencao: `UserPlus` no Lucide e
// `person_add` no Material. A cabeca e o ombro repetem as medidas de `IconePessoas` para os
// dois lerem como familia; o que muda e o SEGUNDO elemento, que la e outra pessoa e aqui e o
// mais. Uma pessoa so, e nao duas, porque Cadastros e quem entrou SOZINHO.
export const IconeCadastro = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
    <path d="M18.5 8v6M15.5 11h6" />
  </Base>
)

// Empresas: o predio com as janelas e a porta. A porta na base e o que impede de ler como
// um grafico de barras, que e a confusao mais comum deste desenho.
export const IconeEmpresas = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M4 21V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v15" />
    <path d="M15 10h3a2 2 0 0 1 2 2v9" />
    <path d="M2.5 21h19" />
    <path d="M7.5 8h1.5M7.5 12h1.5M11 8h1.5M11 12h1.5" />
    <path d="M9 21v-4h2v4" />
  </Base>
)



// Inventario: a prancheta com a presilha no topo. E o instrumento aplicado em papel, que e
// exatamente o que o modulo digitaliza.
export const IconeInventario = (p: PropsIcone) => (
  <Base {...p}>
    <rect x="4.5" y="4.5" width="15" height="16" rx="2" />
    <path d="M9 4.5V3.2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1.3" />
    <path d="M8.5 10.5h7M8.5 14.5h4.5" />
  </Base>
)




// Importar: a seta ENTRANDO na bandeja. A direcao e o significado inteiro do icone, e por
// isso a ponta aponta para BAIXO, para dentro do sistema: a mesma bandeja com a seta para
// cima seria exportar, e os dois viveriam no menu a um item de distancia um do outro.
export const IconeImportar = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 3v10" />
    <path d="m8 9 4 4 4-4" />
    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </Base>
);

// Exportar: o MESMO gesto do importar, com a seta invertida. A bandeja e identica de
// proposito, e so a direcao da seta muda: os dois itens ficam lado a lado no menu, e o que
// distingue um do outro em 20 pixels e justamente para onde a seta aponta. Dois desenhos
// diferentes obrigariam a ler o rotulo para saber qual e qual.
export const IconeExportar = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 13V3" />
    <path d="m8 7 4-4 4 4" />
    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </Base>
);

// Privacidade: o escudo com o cadeado dentro. O escudo sozinho ja e usado por conformidade,
// entao o cadeado e o que diz que aqui o assunto e o dado da pessoa.
export const IconePrivacidade = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 2.7 4.5 5.8v5.6c0 4.6 3.1 8.6 7.5 9.9 4.4-1.3 7.5-5.3 7.5-9.9V5.8Z" />
    <rect x="9.3" y="11" width="5.4" height="4.4" rx="1" />
    <path d="M10.6 11V9.8a1.4 1.4 0 0 1 2.8 0V11" />
  </Base>
)

// Bussola: o que funciona sem rede. A agulha apontada, e nao um ponteiro reto, porque a
// agulha inclinada e o que diz "orientacao" em vez de "relogio".
export const IconeBussola = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15.6 8.4 13.8 13.8 8.4 15.6l1.8-5.4Z" />
  </Base>
)

// Chevron para baixo: o "abre a lista". Angulo de 90 graus e nao mais fechado, porque um
// chevron estreito em 16px le como um acento agudo.
export const IconeChevron = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M6.5 9.5 12 15l5.5-5.5" />
  </Base>
)

/**
 * O MAPA que liga o nome escrito no `ProductConfig` ao desenho.
 *
 * POR QUE UM MAPA E NAO UM `import()` DINAMICO: o mapa é estático, então o empacotador
 * enxerga cada ícone e o `tree-shaking` funciona. Com import dinâmico, todos entrariam no
 * pacote e o ícone só apareceria depois de uma ida à rede, piscando no rail.
 *
 * NOME DESCONHECIDO DEVOLVE `undefined`, e quem chama decide. O `Rail` desenha um ponto no
 * lugar, e não um espaço vazio: item de menu sem nada à esquerda desalinha a lista inteira e
 * lê como defeito de carregamento.
 */

// Lápis: a ação de editar numa linha de lista. Nasceu em 01/09/2026, pedido dele
// ao ver a Base de conhecimento com "Editar / Publicar / Apagar" como links de
// texto: eles quebravam em duas linhas e empurravam a tabela.
//
// O traçado é o do lucide `pencil`, que é o padrão usado no app docia do
// destravaDS, e ele pediu igual. Redesenhado aqui em vez de trazer a biblioteca:
// são dois ícones, e uma dependência de mil deles para usar dois é peso sem uso.
export const IconeLapis = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Base>
)

// Lixeira: a ação de apagar. A tampa separada do corpo é o que a torna
// reconhecível em 14px, que é o tamanho em que ela vive numa linha de tabela.
export const IconeLixeira = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M10 11v6M14 11v6" />
  </Base>
)

// Olho: ver sem alterar. Existe ao lado do lápis porque ler e editar são gestos
// diferentes, e oferecer só o lápis obriga a entrar no formulário para consultar.
export const IconeVer = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </Base>
)

// Publicar: a seta que sobe para fora da caixa. É a ação da Base de conhecimento
// que não é nem editar nem apagar, e sem ícone próprio ela viraria um terceiro
// link de texto ao lado de dois ícones.
export const IconePublicar = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 3v13" />
    <path d="m7 8 5-5 5 5" />
    <path d="M5 21h14" />
  </Base>
)

// Despublicar: a mesma seta, para dentro. O par existe porque uma seta só, com
// a cor mudando, não diz o sentido da ação.
export const IconeDespublicar = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 16V3" />
    <path d="m7 11 5 5 5-5" />
    <path d="M5 21h14" />
  </Base>
)


// ============================================================================
// OS ICONES DO CONSULTORIO · desenhados em 06/09/2026 para o `medico-concierge`
// ----------------------------------------------------------------------------
// NENHUM DELES EXISTIA. O conjunto tinha 45 icones, e nenhum servia para agenda,
// receita, microfone ou recibo: os que havia vinham da aviacao e dos produtos anteriores.
// Desenhar era a unica saida, porque emoji e PROIBIDO na plataforma (regra dura
// de 30/08/2026) e nome de icone que nao resolve degrada para um ponto, ou seja,
// o menu inteiro do produto novo apareceria como uma coluna de pontos.
// ============================================================================

// AGENDA: a folha de calendario, com os DOIS GANCHOS acima da moldura.
//
// Os ganchos sao o que separa "calendario" de "qualquer retangulo com linhas". Sem
// eles, em 20 pixels o desenho vira uma tabela, e o item de menu mais usado do
// consultorio passa a depender do rotulo para ser encontrado.
export const IconeAgenda = (p: PropsIcone) => (
  <Base {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" />
  </Base>
)

// RECEPCAO: o sino de balcao, e nao o sino de aviso.
//
// A plataforma JA TEM um `sino`, e ele significa notificacao. Usar o mesmo desenho
// para a fila de recepcao faria dois itens diferentes carregarem o mesmo simbolo, e
// quem visse o rail leria "notificacoes" duas vezes. Este e o sino de MESA: cupula
// baixa, base larga e o pino em cima, que e como se desenha atendimento presencial.
export const IconeRecepcao = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M12 6.5V5" />
    <path d="M5 16.5a7 7 0 0 1 14 0" />
    <path d="M3 16.5h18" />
    <path d="M6 20h12" />
  </Base>
)

// ATENDIMENTO: o estetoscopio, que e o simbolo universal do ato clinico.
//
// O tubo em U com a campanula na ponta e reconhecivel mesmo pequeno, e e o unico
// desenho que diz "consulta" sem dizer "hospital". Cruz vermelha diria emergencia,
// e nao e disto que este produto trata.
export const IconeAtendimento = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M5 3v5a4 4 0 0 0 8 0V3" />
    <path d="M4 3h2M12 3h2" />
    <path d="M9 12v2a5 5 0 0 0 10 0v-1" />
    <circle cx="19" cy="10" r="2" />
  </Base>
)

// MICROFONE: a captacao de audio da consulta, que alimenta o AI Scribe.
//
// A capsula arredondada MAIS o arco embaixo, e nunca so a capsula: sozinha ela lê
// como pilha ou como remedio. O arco e a haste sao o que fazem o olho completar o
// microfone, e custam dois `path`.
export const IconeMicrofone = (p: PropsIcone) => (
  <Base {...p}>
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3M9 21h6" />
  </Base>
)

// PRESCRICAO: a folha com a orelha dobrada e a cruz da saude.
//
// A orelha dobrada e o que diz "documento". A cruz e o que diz "de saude". Sem a
// cruz este icone colidiria com `registros`, que ja e uma folha, e dois itens do
// mesmo menu ficariam indistinguiveis a distancia de um braco.
export const IconePrescricao = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" />
    <path d="M12 11v6M9 14h6" />
  </Base>
)

// ASSINATURA: a caneta inclinada sobre a linha de base.
//
// A LINHA EMBAIXO e o que transforma "caneta" em "assinar". Sem ela o desenho e o
// mesmo de editar, e no fluxo deste produto editar e assinar sao coisas juridicamente
// diferentes: uma a secretaria pode fazer, a outra so o medico.
export const IconeAssinatura = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M3 21h18" />
    <path d="M5 17.2 15.6 6.6a2.1 2.1 0 0 1 3 3L8 20.2l-4 .8z" />
  </Base>
)

// REEMBOLSO: o recibo de borda serrilhada com a seta de volta.
//
// A serrilha de baixo e a assinatura visual de recibo, herdada do papel que sai da
// impressora termica. A seta apontando para tras e o que diz REEMBOLSO e nao apenas
// recibo: o dinheiro faz o caminho de volta ao paciente.
export const IconeReembolso = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M6 3h12v17l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4-2 1.4z" />
    <path d="M9 8h6" />
    <path d="M14.5 12.5H9.5M11.5 10.5 9.5 12.5l2 2" />
  </Base>
)

// FINANCEIRO: a moeda com o cifrao.
//
// Circulo e nao retangulo de propósito: o retangulo com faixa ja e o cartao
// (`IconePagamento`), e os dois itens convivem no mesmo produto. Formas diferentes
// para conceitos diferentes e o que permite ler o rail sem parar para pensar.
export const IconeFinanceiro = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v10" />
    <path d="M14.6 9.4c0-1-1.2-1.6-2.6-1.6s-2.6.7-2.6 1.9S10.6 12 12 12s2.6.6 2.6 1.8-1.2 1.8-2.6 1.8-2.6-.6-2.6-1.6" />
  </Base>
)

// CONCIERGE: a estrela cheia, para a ficha de preferencias VIP.
//
// PREENCHIDA e nao vazada, e e a unica excecao de preenchimento do conjunto: a ficha
// VIP e o unico lugar do produto que marca distincao, e estrela vazada lê como
// "favoritar", que e outra acao. O `fill` vem de `currentColor` para o icone continuar
// respeitando o tema.
export const IconeConcierge = (p: PropsIcone) => (
  <Base {...p}>
    <path
      d="m12 3.2 2.6 5.4 5.9.9-4.3 4.1 1 5.9-5.2-2.8-5.2 2.8 1-5.9L3.5 9.5l5.9-.9z"
      fill="currentColor"
      strokeWidth={1.2}
    />
  </Base>
)

// CONVERSA: o balao de mensagem com a ponta embaixo a esquerda.
//
// As DUAS LINHAS dentro do balao nao sao enfeite: elas dizem que ha texto, e e o que
// separa este desenho de um simples retangulo arredondado. A ponta na base esquerda e
// a convencao de mensagem recebida, que e o caso da secretaria.
export const IconeConversa = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M20 15a2 2 0 0 1-2 2H8l-4 3.5V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
    <path d="M8 9h8M8 12.5h5" />
  </Base>
)

// RELOGIO: o tempo de espera do paciente na recepcao.
//
// Os ponteiros marcam uma hora QUALQUER que nao seja 12:00 nem 3:00, porque nessas
// posicoes eles se sobrepoem ao circulo ou formam um angulo reto que lê como mira. A
// posicao escolhida deixa os dois visiveis e separados em 20 pixels.
export const IconeRelogio = (p: PropsIcone) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.2l3.4 2" />
  </Base>
)

// PRONTUARIO: a prancheta com a cruz da saude.
//
// A PRESILHA no topo e o que diz prancheta, e ela precisa passar POR CIMA da borda da
// folha, senao o olho lê duas formas empilhadas em vez de uma so. A cruz repete a de
// `IconePrescricao` de propósito: as duas telas falam do mesmo domínio clinico.
export const IconeProntuario = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
    <rect x="9" y="2" width="6" height="4" rx="1" />
    <path d="M12 11v6M9 14h6" />
  </Base>
)

// PAGAMENTO: o cartao com a tarja e o comeco do numero.
//
// A TARJA HORIZONTAL logo abaixo do topo e o que faz o retangulo virar cartao. O
// tracinho de baixo sugere o numero impresso sem tentar desenhar digitos, que em 24
// pixels viram ruido.
export const IconePagamento = (p: PropsIcone) => (
  <Base {...p}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 9.5h20" />
    <path d="M6 15h4" />
  </Base>
)

// RELATORIOS: o eixo com tres barras de alturas diferentes.
//
// AS TRES ALTURAS DIFERENTES sao o que diz "comparacao", e comparar e o que um relatorio faz.
// Barras iguais leriam como grade ou lista. O eixo em L ancora as barras no chao, senao elas
// flutuam e viram tres tracos soltos em 24 pixels. Entrou em 16/09/2026 com o menu Relatorios
// do medicina, porque nenhum desenho do conjunto dizia isso.
export const IconeRelatorios = (p: PropsIcone) => (
  <Base {...p}>
    <path d="M3 3v18h18" />
    <path d="M8 17v-5M13 17V8M18 17v-8" />
  </Base>
)

// CHECK · o tique da lista de usos da IA na tela de entrada.
//
// POR QUE ELE PRECISOU EXISTIR, e a razao e regra dura: o fundador mandou em 07/09/2026 que
// a tela de entrada de TODOS os apps siga o padrao da lista de checks, e EMOJI E PROIBIDO na
// plataforma inteira. O caractere Unicode tambem nao serve, e ele e o que mais escapa: `✓` e
// `✔` nao estao na faixa de emoji, entao passam por varredura ingenua, e nem toda fonte os
// tem: onde falta, o navegador desenha um retangulo vazio no lugar do tique.
//
// SEM PRECONCEITO DE COR: ele herda `currentColor`, entao quem o desenha decide a cor pelo
// texto em volta, e ele acompanha tema claro e escuro sem duplicar arquivo.
function IconeCheck(props: PropsIcone) {
  return (
    <Base {...props}>
      {/* Um traco so, do canto alto direito ate a ponta baixa e de volta para cima. E o
          tique de uma linha, e nao um V fechado: em 16px o V le como letra. */}
      <path d="M20 6 9 17l-5-5" />
    </Base>
  )
}

export const ICONES: Readonly<Record<string, ComponentType<SVGProps<SVGSVGElement>>>> =
  Object.freeze({
    // Os doze módulos da operação do prontidão, na ordem do menu.
    // As acoes de linha de lista, somadas em 01/09/2026: elas substituem os
    // links de texto "Editar / Publicar / Apagar", que quebravam em duas linhas.
    // Ordenar linha de lista, somados em 09/09/2026 para o arrastar da fila da recepcao:
    // eles sao o SEGUNDO caminho do mesmo gesto, para quem nao consegue arrastar.
    'seta-cima': IconeSetaCima,
    'seta-baixo': IconeSetaBaixo,
    lapis: IconeLapis,
    lixeira: IconeLixeira,
    ver: IconeVer,
    publicar: IconePublicar,
    despublicar: IconeDespublicar,

    voar: IconeVoar,
    painel: IconePainel,
    voos: IconeVoos,
    diario: IconeDiario,
    frota: IconeFrota,
    tripulacao: IconeTripulacao,
    mapa: IconeMapa,
    meteorologia: IconeMeteorologia,
    notam: IconeNotam,
    seguranca: IconeSeguranca,
    conformidade: IconeConformidade,
    normas: IconeNormas,
    // Os dois itens que são o próprio sistema, e não a operação.
    usuarios: IconeUsuarios,
    chaves: IconeIa,
    // O VOE nomeia este item como `ia`, porque a tela dele e chaves E extrato de consumo.
    // Mesmo desenho, segundo nome: duplicar o SVG faria os dois envelhecerem separados.
    ia: IconeIa,

    // Os do chassi, que o chassi usa por conta própria e o produto não nomeia.
    // O `check` entra aqui, e nao no bloco de um produto, porque quem o desenha e a TELA DE
    // ENTRADA da plataforma: nenhum manifesto o nomeia, e ele vale para todos os apps.
    check: IconeCheck,
    sobre: IconeSobre,
    menu: IconeMenu,
    fechar: IconeFechar,
    // UMA chave para os dois estados: o desenho e o mesmo aberto e fechado, e quem diz o
    // estado e o `aria-label` de quem usa. Ver o comentario do `IconePainelLateral`.
    painelLateral: IconePainelLateral,
    voltar: IconeVoltar,
    sol: IconeSol,
    lua: IconeLua,
    sair: IconeSair,
    busca: IconeBusca,
    // Cadastro, inventario e privacidade. NASCERAM para um produto que saiu da plataforma
    // em 07/09/2026, e continuam VIVOS: os tres sao nomeados pelo manifesto do
    // admin (`admin.config.ts:63,77,84`). Os outros cinco do bloco original eram orfaos e
    // sairam junto. Antes de apagar qualquer um daqui, medir quem o NOMEIA: o vinculo e por
    // string no manifesto, e nao por import, entao o typecheck NAO acusa a remocao errada.
    empresas: IconeEmpresas,
    inventario: IconeInventario,
    importar: IconeImportar,
    exportar: IconeExportar,
    privacidade: IconePrivacidade,
    // Os do chassi e do manifesto, que qualquer produto nomeia.
    sino: IconeSino,
    ajuda: IconeAjuda,
    cadeado: IconeCadeado,
    auditoria: IconeAuditoria,
    idioma: IconeIdioma,
    registros: IconeRegistros,
    ajustes: IconeAjustes,
    engrenagem: IconeEngrenagem,
    pessoas: IconePessoas,
    cadastro: IconeCadastro,
    bussola: IconeBussola,
    // Os do `medico-concierge`, o ciclo do consultorio particular. Desenhados em
    // 06/09/2026: nenhum deles existia, e emoji e proibido na plataforma.
    agenda: IconeAgenda,
    recepcao: IconeRecepcao,
    atendimento: IconeAtendimento,
    microfone: IconeMicrofone,
    prescricao: IconePrescricao,
    assinatura: IconeAssinatura,
    reembolso: IconeReembolso,
    financeiro: IconeFinanceiro,
    concierge: IconeConcierge,
    conversa: IconeConversa,
    relogio: IconeRelogio,
    prontuario: IconeProntuario,
    pagamento: IconePagamento,
    relatorios: IconeRelatorios,
    chevron: IconeChevron,
  })

/** O nome de um ícone do conjunto. Tipo aberto de propósito: o `ProductConfig` de um produto
 *  novo pode nomear um ícone que ainda não existe, e o certo é o rail degradar para o ponto
 *  em vez de o build quebrar num produto que nem é o que está sendo editado. */
export type NomeIcone = string

/** Resolve o nome no desenho. `undefined` quando o nome não existe no conjunto. */
export function icone(nome: string): ComponentType<SVGProps<SVGSVGElement>> | undefined {
  return ICONES[nome]
}
